import { parse } from 'papaparse';
import type { ZodType } from 'zod';
import {
  companiesImport,
  usersImport,
  pointsImport,
  perfsImport,
  targetsImport
} from '@r/api/generated/client.ts';
import type {
  ImportCompanyDtoCompanyImportModel,
  ImportUserDtoUserImportModel,
  ImportPointDtoPointImportModel,
  ImportPerformanceDtoPerformanceImportModel,
  ImportLogicTypeTargetStatusDtoTargetsImportModel,
  CompanyCompanyImportModel,
  UserUserImportModel,
  PointPointImportModel,
  PerformancePerformanceImportModel,
  LogicTypeTargetStatusTargetsImportModel,
  UserWorkspaceUserImportModel,
  UserWorkspaceUserImportModelMaxRole
} from '@r/api/generated/client.schemas.ts';
import { parse as parseDate, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  dedupeErrors,
  normalizeEmpty,
  mapViolationsToErrorsWithIndexMap,
  resolveErrorValue
} from './import-utils.ts';
import { isOrvalError } from '@r/api/orval-error-helper.ts';

// Utilitaire pour transformer referenceQuantity de string vers number
const transformReferenceQuantity = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value) === '') {
    return null;
  }
  const parsed = Number.parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// Schémas d'entrée acceptés selon l'endpoint d'import visé
type ImportRow =
  | CompanyCompanyImportModel
  | UserUserImportModel
  | PointPointImportModel
  | PerformancePerformanceImportModel
  | LogicTypeTargetStatusTargetsImportModel;
type bodySchemaType = ZodType<ImportRow>;

/** Types de mouvements négatifs pour lesquels une expiration n'a pas de sens. */
const pointTypesWithoutExpiration = new Set(['SPENT', 'EXPIRED', 'CANCELED']);

/** Convertit les cellules CSV vides en valeurs optionnelles avant validation. */
const transformCsvValue = (value: string | number) =>
  normalizeEmpty(value) as string | number | undefined;

// Type utile pour reformater les users: certaines colonnes CSV peuvent être "aplatises"
// et doivent être remontées dans la structure imbriquée userWorkspace attendue par l'API
type userImport = UserUserImportModel;
type UserImportZ = UserUserImportModel;
type UserWorkspaceImportZ = UserWorkspaceUserImportModel;
type FlatUserRow = Omit<UserImportZ, 'userWorkspace'> & {
  userWorkspace?: Partial<UserWorkspaceUserImportModel>;
  uniqueCode?: string;
  companyUniqueCode?: string | null;
  maxRole?: UserWorkspaceUserImportModelMaxRole;
  phone?: string | null;
  comment?: string | null;
};

// Liste blanche des endpoints supportés pour cet import CSV
type urls =
  | '/admin/companies/import'
  | '/admin/users/import'
  | '/admin/points/import'
  | '/admin/performances/import'
  | '/admin/targets/import';

// Format d'une erreur utilisateur remontée par le parser (client ou serveur)
export interface ImportError {
  message: string;
  line?: number;
  field?: string;
  value?: string;
  error?: string;
  originalData?: Record<string, unknown>;
}

// Résultat global de l'opération d'import (succès/échec + messages)
export interface ImportAnalysis {
  totalLines: number;
  errorLines: number;
  importableLines: number;
  errorPercentage: number;
}

export interface ImportResult {
  success: boolean;
  message?: string;
  errors?: ImportError[];
  isPreview?: boolean;
  analysis?: ImportAnalysis;
}

const buildImportAnalysis = (errors: ImportError[], totalLines: number): ImportAnalysis => {
  const errorLineNumbers = new Set<number>();

  errors.forEach((error, index) => {
    if (typeof error.line === 'number') {
      errorLineNumbers.add(error.line);
      return;
    }

    errorLineNumbers.add(-(index + 1));
  });

  const errorLines = Math.min(errorLineNumbers.size, totalLines);
  const importableLines = Math.max(totalLines - errorLines, 0);
  const errorPercentage = totalLines > 0 ? Math.round((errorLines / totalLines) * 100) : 0;

  return {
    totalLines,
    errorLines,
    importableLines,
    errorPercentage
  };
};

export function useFileParserWithErrors(bodySchema: bodySchemaType, url: urls) {
  const { t } = useTranslation();
  const FIRST_DATA_LINE_NUMBER = 2;

  // Mémoire des erreurs de la précédente tentative pour permettre un import forcé
  const [previousErrors, setPreviousErrors] = useState<ImportError[]>([]);

  // Lance le parsing + validation + envoi. Si forceImport=true, on exclut les lignes invalides
  const onParse = (files: File[], forceImport = false): Promise<ImportResult> => {
    return new Promise((resolve, reject) => {
      parse<ImportRow>(files[0], {
        // CSV courants Windows
        encoding: 'ISO-8859-1',
        // Entête présent en première ligne
        header: true,
        // Auto-détection du délimiteur (virgule ou point-virgule)
        delimiter: '',
        complete: (result) => {
          const data = result.data;

          if (result.errors.length) {
            // Erreurs de parsing "brutes" (mauvais CSV, colonnes décalées, etc.)
            reject(t('import.process.parsing.error'));
          }

          // Collecte des erreurs de validation locale (Zod) par ligne/champ
          const formatedErrors: ImportError[] = [];

          /**
           * Convertit les formats de date courants en ISO UTC.
           * Une expiration sans heure est placée en fin de journée.
           */
          function formatDateTime(dateString: string, endOfDay = false): string {
            if (!dateString) return dateString;

            const cleaned = String(dateString).trim();
            const dateOnlyPattern = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{2}\/\d{2}\/\d{2})$/;

            // 1) Essaye les formats explicites les plus courants
            const formats = ['dd/MM/yyyy', 'dd/MM/yy', 'yyyy-MM-dd', 'MM/dd/yyyy'];
            for (const format of formats) {
              try {
                const parsedDate = parseDate(cleaned, format, new Date());
                if (isValid(parsedDate)) {
                  // Conserve la date telle qu'attendue côté utilisateur (UTC minuit) pour éviter les décalages
                  const normalizedUtcDate = new Date(
                    Date.UTC(
                      parsedDate.getFullYear(),
                      parsedDate.getMonth(),
                      parsedDate.getDate(),
                      endOfDay ? 23 : parsedDate.getHours(),
                      endOfDay ? 59 : parsedDate.getMinutes(),
                      endOfDay ? 59 : parsedDate.getSeconds(),
                      endOfDay ? 999 : parsedDate.getMilliseconds()
                    )
                  );
                  return normalizedUtcDate.toISOString(); // format ISO strict attendu par z.string().datetime()
                }
              } catch {}
            }

            // 2) Si la chaîne est déjà un ISO valide ou un format que Date.parse comprend, on le normalise
            const epoch = Date.parse(cleaned);
            if (!Number.isNaN(epoch)) {
              const parsed = new Date(epoch);
              if (isValid(parsed)) {
                if (dateOnlyPattern.test(cleaned)) {
                  // Les dates d'expiration couvrent toute la journée sélectionnée.
                  const normalizedUtcDate = new Date(
                    Date.UTC(
                      parsed.getFullYear(),
                      parsed.getMonth(),
                      parsed.getDate(),
                      endOfDay ? 23 : 0,
                      endOfDay ? 59 : 0,
                      endOfDay ? 59 : 0,
                      endOfDay ? 999 : 0
                    )
                  );
                  return normalizedUtcDate.toISOString();
                }
                return parsed.toISOString();
              }
            }

            // 3) En dernier recours on renvoie la valeur nettoyée (pour que l'erreur soit explicite)
            return cleaned;
          }

          /** Normalise le montant et les dates d'une ligne de points avant sa validation. */
          function transformPointImportData(
            pointData: PointPointImportModel
          ): PointPointImportModel {
            return {
              ...pointData,
              amount: Number.parseInt(String(pointData.amount), 10),
              valueDate: pointData.valueDate
                ? formatDateTime(pointData.valueDate)
                : pointData.valueDate,
              expirationDate: pointData.expirationDate
                ? formatDateTime(pointData.expirationDate, true)
                : pointData.expirationDate
            };
          }

          let performanceData: PerformancePerformanceImportModel;
          let pointData: PointPointImportModel;
          let targetData: LogicTypeTargetStatusTargetsImportModel;

          // Parcours des lignes pour appliquer les conversions spécifiques et valider localement
          data.forEach((rowData, index) => {
            let transformedData = { ...rowData };

            if (url === '/admin/users/import') {
              // Support des colonnes "aplatises" depuis le CSV pour reconstruire userWorkspace
              const flatUniqueCode = (transformedData as FlatUserRow).uniqueCode;
              const flatCompanyUniqueCode = (transformedData as FlatUserRow).companyUniqueCode;
              const flatMaxRole = (transformedData as FlatUserRow).maxRole;
              const flatPhone = (transformedData as FlatUserRow).phone;
              const flatComment = (transformedData as FlatUserRow).comment;

              if (
                !(transformedData as FlatUserRow).userWorkspace &&
                (flatUniqueCode !== undefined ||
                  flatCompanyUniqueCode !== undefined ||
                  flatMaxRole !== undefined ||
                  flatPhone !== undefined ||
                  flatComment !== undefined)
              ) {
                const userWs: Partial<UserWorkspaceImportZ> = {};
                if (flatUniqueCode !== undefined)
                  userWs.uniqueCode = flatUniqueCode as UserWorkspaceImportZ['uniqueCode'];
                if (flatCompanyUniqueCode !== undefined)
                  userWs.companyUniqueCode =
                    flatCompanyUniqueCode as UserWorkspaceImportZ['companyUniqueCode'];
                if (flatMaxRole !== undefined)
                  userWs.maxRole = flatMaxRole as UserWorkspaceImportZ['maxRole'];
                if (flatPhone !== undefined)
                  userWs.phone = flatPhone as UserWorkspaceImportZ['phone'];
                if (flatComment !== undefined)
                  userWs.comment = flatComment as UserWorkspaceImportZ['comment'];

                (transformedData as FlatUserRow).userWorkspace =
                  userWs as UserImportZ['userWorkspace'];
              }

              // Normalisation emailNotification: accepte Yes/No/true/false/1/0
              const rawEmailField = (transformedData as Record<string, unknown>).emailNotification;

              if (rawEmailField !== undefined) {
                const normalized = String(rawEmailField).trim().toLowerCase();
                if (normalized === 'yes' || normalized === 'no') {
                  transformedData = {
                    ...(transformedData as Record<string, unknown>),
                    emailNotification: normalized === 'yes'
                  } as typeof transformedData;
                }
              }
            }

            switch (url) {
              case '/admin/points/import':
                pointData = transformPointImportData(transformedData as PointPointImportModel);
                transformedData = pointData;

                if (
                  pointTypesWithoutExpiration.has(pointData.typeLabel) &&
                  pointData.expirationDate
                ) {
                  const error = t('import.points.expiration.negative.error');
                  formatedErrors.push({
                    message: t('file.parser.error.line', {
                      line: index + FIRST_DATA_LINE_NUMBER,
                      field: 'expirationDate',
                      error
                    }),
                    line: index + FIRST_DATA_LINE_NUMBER,
                    field: 'expirationDate',
                    value:
                      (rowData as unknown as Record<string, unknown>).expirationDate?.toString() ??
                      '',
                    error,
                    originalData: rowData as unknown as Record<string, unknown>
                  });
                }
                break;

              case '/admin/performances/import':
                performanceData = transformedData as PerformancePerformanceImportModel;

                // Performances: décimaux avec virgule -> float, et date normalisée
                for (const key in performanceData) {
                  if (key === 'amount' && performanceData.amount !== undefined) {
                    const normalizedAmount = performanceData.amount.toString().replace(',', '.');
                    performanceData.amount = Number.parseFloat(normalizedAmount);
                  } else if (key === 'realizedAt') {
                    performanceData.realizedAt = formatDateTime(performanceData.realizedAt);
                  } else if (key === 'referenceQuantity') {
                    performanceData.referenceQuantity = transformReferenceQuantity(
                      performanceData.referenceQuantity
                    );
                  }
                }

                transformedData = performanceData;
                console.info('Final transformedData for performance:', transformedData);
                break;

              case '/admin/targets/import':
                targetData = transformedData as LogicTypeTargetStatusTargetsImportModel;

                // Targets: valeur numérique
                for (const key in targetData) {
                  if (key === 'targetValue') {
                    targetData.targetValue = Number.parseFloat(String(targetData.targetValue));
                  }
                }

                transformedData = targetData;
                break;
            }

            // Validation locale contre le schéma Zod, formatage d'erreurs par ligne/champ
            const result = bodySchema.safeParse(transformedData);
            if (!result.success) {
              const errors = result?.error?.format();
              for (const [key, value] of Object.entries(errors)) {
                if (typeof value === 'object' && value !== null && '_errors' in value)
                  for (const error of value._errors) {
                    formatedErrors.push({
                      message: t('file.parser.error.line', {
                        line: index + FIRST_DATA_LINE_NUMBER,
                        field: key,
                        error
                      }),
                      line: index + FIRST_DATA_LINE_NUMBER,
                      field: key,
                      value: resolveErrorValue(
                        key,
                        (rowData as unknown as Record<string, unknown>)[key],
                        t
                      ),
                      error: error,
                      originalData: rowData as unknown as Record<string, unknown>
                    });
                  }
              }
            }

            if (url === '/admin/performances/import' && forceImport) {
              const perfData = transformedData as PerformancePerformanceImportModel;

              if (perfData.companyUniqueCode && perfData.companyUniqueCode.length > 0) {
              }
            }
          });

          // En première passe, on analyse systématiquement le fichier sans écrire en base:
          // - on isole les lignes valides localement
          // - on exécute un dry-run côté serveur pour récupérer d'éventuelles erreurs backend
          if (!forceImport) {
            const errorLineIndices = new Set(
              formatedErrors.map((error) =>
                typeof error.line === 'number'
                  ? Math.max(error.line - FIRST_DATA_LINE_NUMBER, 0)
                  : 0
              )
            );
            const indexMapValid: number[] = [];
            let validRows: ImportRow[] = [];
            for (const [index, row] of data.entries()) {
              if (!errorLineIndices.has(index)) {
                indexMapValid.push(index);
                validRows.push(row);
              }
            }

            // Harmoniser la forme des données pour le serveur (notamment users)
            validRows = validRows.map((rowData) => {
              let transformedData = { ...rowData } as typeof rowData;

              if (url === '/admin/users/import') {
                const flatUniqueCode = (transformedData as FlatUserRow).uniqueCode;
                const flatCompanyUniqueCode = (transformedData as FlatUserRow).companyUniqueCode;
                const flatMaxRole = (transformedData as FlatUserRow).maxRole;
                const flatPhone = (transformedData as FlatUserRow).phone;
                const flatComment = (transformedData as FlatUserRow).comment;

                if (
                  !(transformedData as FlatUserRow).userWorkspace &&
                  (flatUniqueCode !== undefined ||
                    flatCompanyUniqueCode !== undefined ||
                    flatMaxRole !== undefined ||
                    flatPhone !== undefined ||
                    flatComment !== undefined)
                ) {
                  const userWs: Partial<UserWorkspaceImportZ> = {};
                  if (flatUniqueCode !== undefined)
                    userWs.uniqueCode = flatUniqueCode as UserWorkspaceImportZ['uniqueCode'];
                  if (flatCompanyUniqueCode !== undefined)
                    userWs.companyUniqueCode =
                      flatCompanyUniqueCode as UserWorkspaceImportZ['companyUniqueCode'];
                  if (flatMaxRole !== undefined)
                    userWs.maxRole = flatMaxRole as UserWorkspaceImportZ['maxRole'];
                  if (flatPhone !== undefined)
                    userWs.phone = flatPhone as UserWorkspaceImportZ['phone'];
                  if (flatComment !== undefined)
                    userWs.comment = flatComment as UserWorkspaceImportZ['comment'];

                  (transformedData as unknown as FlatUserRow).userWorkspace =
                    userWs as UserImportZ['userWorkspace'];
                }

                // Normalisation emailNotification sur les lignes valides également
                const rawEmailField = (transformedData as unknown as Record<string, unknown>)
                  .emailNotification;

                if (rawEmailField !== undefined) {
                  const normalized = String(rawEmailField).trim().toLowerCase();
                  if (normalized === 'yes' || normalized === 'no') {
                    transformedData = {
                      ...(transformedData as unknown as Record<string, unknown>),
                      emailNotification: normalized === 'yes'
                    } as typeof transformedData;
                  }
                }

                const {
                  uniqueCode: _omitUniqueCode,
                  companyUniqueCode: _omitCompanyUniqueCode,
                  maxRole: _omitMaxRole,
                  phone: _omitPhone,
                  comment: _omitComment,
                  ...restUser
                } = transformedData as unknown as FlatUserRow;
                transformedData = restUser as typeof rowData;
              }

              switch (url) {
                case '/admin/points/import': {
                  const pointData = transformPointImportData(
                    transformedData as PointPointImportModel
                  );
                  transformedData = pointData as typeof rowData;
                  break;
                }

                case '/admin/performances/import': {
                  const performanceData = transformedData as PerformancePerformanceImportModel;
                  for (const key in performanceData) {
                    if (key === 'amount' && performanceData.amount !== undefined) {
                      const normalizedAmount = performanceData.amount.toString().replace(',', '.');
                      performanceData.amount = Number.parseFloat(normalizedAmount);
                    } else if (key === 'realizedAt') {
                      performanceData.realizedAt = formatDateTime(performanceData.realizedAt);
                    } else if (key === 'referenceQuantity') {
                      performanceData.referenceQuantity = transformReferenceQuantity(
                        performanceData.referenceQuantity
                      );
                    }
                  }
                  transformedData = performanceData as typeof rowData;
                  break;
                }

                case '/admin/targets/import': {
                  const targetData = transformedData as LogicTypeTargetStatusTargetsImportModel;
                  for (const key in targetData) {
                    if (key === 'targetValue') {
                      targetData.targetValue = Number.parseFloat(String(targetData.targetValue));
                    }
                  }
                  transformedData = targetData as typeof rowData;
                  break;
                }
              }

              return transformedData as typeof rowData;
            });

            if (validRows.length === 0) {
              setPreviousErrors(formatedErrors);
              resolve({
                success: false,
                errors: formatedErrors,
                isPreview: true,
                analysis: buildImportAnalysis(formatedErrors, data.length)
              });
              return;
            }

            // Dry-run via Orval avec header 'dry-run'
            const dryRun = async () => {
              switch (url) {
                case '/admin/companies/import':
                  return await companiesImport(
                    { data: validRows } as ImportCompanyDtoCompanyImportModel,
                    { headers: { 'dry-run': 'true' } }
                  );
                case '/admin/users/import':
                  return await usersImport({ data: validRows } as ImportUserDtoUserImportModel, {
                    headers: { 'dry-run': 'true' }
                  });
                case '/admin/points/import':
                  return await pointsImport({ data: validRows } as ImportPointDtoPointImportModel, {
                    headers: { 'dry-run': 'true' }
                  });
                case '/admin/performances/import':
                  return await perfsImport(
                    { data: validRows } as ImportPerformanceDtoPerformanceImportModel,
                    { headers: { 'dry-run': 'true' } }
                  );
                case '/admin/targets/import':
                  return await targetsImport(
                    { data: validRows } as ImportLogicTypeTargetStatusDtoTargetsImportModel,
                    { headers: { 'dry-run': 'true' } }
                  );
              }
            };
            dryRun()
              .then(() => {
                setPreviousErrors(formatedErrors);
                resolve({
                  success: false,
                  errors: formatedErrors,
                  isPreview: true,
                  analysis: buildImportAnalysis(formatedErrors, data.length)
                });
              })
              .catch((error) => {
                if (isOrvalError(error) && error.response?.status === 422) {
                  const apiErrors: ImportError[] = [];
                  const violations = (
                    error.response.data as {
                      violations?: Array<{ property: string; detail: string }>;
                    }
                  )?.violations;
                  if (violations) {
                    // Mapper les violations Orval vers ViolationLike (ajouter message depuis detail)
                    const violationsLike = violations.map((v) => ({ ...v, message: v.detail }));
                    apiErrors.push(
                      ...mapViolationsToErrorsWithIndexMap(
                        violationsLike,
                        indexMapValid,
                        data as unknown as Record<string, unknown>[],
                        t
                      )
                    );
                  }

                  // Fusion + déduplication des erreurs locales et serveur
                  const combinedErrors = dedupeErrors([...formatedErrors, ...apiErrors]);
                  setPreviousErrors(combinedErrors);

                  resolve({
                    success: false,
                    errors: combinedErrors,
                    isPreview: true,
                    analysis: buildImportAnalysis(combinedErrors, data.length)
                  });
                } else {
                  reject(t('import.process.error'));
                }
              });
            return;
          }
          // À partir d'ici: pas d'erreur locale bloquante OU import forcé
          let dataToSend = data;
          let indexMapToSend: number[] = data.map((_, i) => i);

          if (forceImport) {
            // En import confirmé: on retire les lignes connues en erreur (locales ou dry-run serveur)
            const errorsToExclude = dedupeErrors([...previousErrors, ...formatedErrors]);
            const errorLineIndices = new Set(
              errorsToExclude.map((error) =>
                typeof error.line === 'number'
                  ? Math.max(error.line - FIRST_DATA_LINE_NUMBER, 0)
                  : 0
              )
            );

            if (errorLineIndices.size > 0) {
              dataToSend = data.filter((_, index) => !errorLineIndices.has(index));
              indexMapToSend = indexMapToSend.filter(
                (originalIdx) => !errorLineIndices.has(originalIdx)
              );
            }
          }

          if (forceImport && dataToSend.length === 0) {
            // Rien à importer après exclusion -> on renvoie l'information utilisateur
            resolve({
              success: false,
              message: t('import.process.no.lines.imported', { totalLines: data.length }),
              errors: formatedErrors.length ? formatedErrors : previousErrors
            });
            return;
          }

          // Prépare les données finales pour l'API, avec normalisations par endpoint
          const transformedDataForApi = dataToSend.map((rowData) => {
            let transformedData = { ...rowData };

            if (url === '/admin/users/import') {
              // Reconstitution de userWorkspace depuis les champs "aplatis"
              const flatUniqueCode = (transformedData as FlatUserRow).uniqueCode;
              const flatCompanyUniqueCode = (transformedData as FlatUserRow).companyUniqueCode;
              const flatMaxRole = (transformedData as FlatUserRow).maxRole;
              const flatPhone = (transformedData as FlatUserRow).phone;
              const flatComment = (transformedData as FlatUserRow).comment;

              if (
                !(transformedData as FlatUserRow).userWorkspace &&
                (flatUniqueCode !== undefined ||
                  flatCompanyUniqueCode !== undefined ||
                  flatMaxRole !== undefined ||
                  flatPhone !== undefined ||
                  flatComment !== undefined)
              ) {
                const userWs: Partial<UserWorkspaceImportZ> = {};
                if (flatUniqueCode !== undefined)
                  userWs.uniqueCode = flatUniqueCode as UserWorkspaceImportZ['uniqueCode'];
                if (flatCompanyUniqueCode !== undefined)
                  userWs.companyUniqueCode =
                    flatCompanyUniqueCode as UserWorkspaceImportZ['companyUniqueCode'];
                if (flatMaxRole !== undefined)
                  userWs.maxRole = flatMaxRole as UserWorkspaceImportZ['maxRole'];
                if (flatPhone !== undefined)
                  userWs.phone = flatPhone as UserWorkspaceImportZ['phone'];
                if (flatComment !== undefined)
                  userWs.comment = flatComment as UserWorkspaceImportZ['comment'];

                (transformedData as FlatUserRow).userWorkspace =
                  userWs as UserImportZ['userWorkspace'];
              }

              // Normalisation emailNotification pour l'envoi final
              const rawEmailField = (transformedData as Record<string, unknown>).emailNotification;
              if (rawEmailField !== undefined) {
                const normalized = String(rawEmailField).trim().toLowerCase();
                if (normalized === 'yes' || normalized === 'no') {
                  transformedData = {
                    ...(transformedData as Record<string, unknown>),
                    emailNotification: normalized === 'yes'
                  } as typeof transformedData;
                }
              }

              const {
                uniqueCode: _omitUniqueCode,
                companyUniqueCode: _omitCompanyUniqueCode,
                maxRole: _omitMaxRole,
                phone: _omitPhone,
                comment: _omitComment,
                ...restUser
              } = transformedData as FlatUserRow;
              transformedData = restUser as typeof transformedData;
            }

            switch (url) {
              case '/admin/points/import': {
                const pointData = transformPointImportData(
                  transformedData as PointPointImportModel
                );
                transformedData = pointData;
                break;
              }

              case '/admin/performances/import': {
                const performanceData = transformedData as PerformancePerformanceImportModel;
                // Virgule -> point pour décimal + date normalisée
                for (const key in performanceData) {
                  if (key === 'amount' && performanceData.amount !== undefined) {
                    const normalizedAmount = performanceData.amount.toString().replace(',', '.');
                    performanceData.amount = Number.parseFloat(normalizedAmount);
                  } else if (key === 'realizedAt') {
                    performanceData.realizedAt = formatDateTime(performanceData.realizedAt);
                  } else if (key === 'referenceQuantity') {
                    performanceData.referenceQuantity = transformReferenceQuantity(
                      performanceData.referenceQuantity
                    );
                  }
                }
                transformedData = performanceData;
                break;
              }

              case '/admin/targets/import': {
                const targetData = transformedData as LogicTypeTargetStatusTargetsImportModel;
                // Valeur numérique sur targetValue
                for (const key in targetData) {
                  if (key === 'targetValue') {
                    targetData.targetValue = Number.parseFloat(String(targetData.targetValue));
                  }
                }
                transformedData = targetData;
                break;
              }
            }

            return transformedData;
          });

          if (url === '/admin/users/import') {
            // Assure la structure attendue par l'API côté users (supprime les champs "aplatis")
            const dataToImport: userImport[] = [];
            for (const user of transformedDataForApi as UserUserImportModel[]) {
              const { userWorkspace, ...rest } = user;
              dataToImport.push({
                ...rest,
                userWorkspace: {
                  uniqueCode: userWorkspace.uniqueCode,
                  companyUniqueCode: userWorkspace.companyUniqueCode,
                  maxRole: userWorkspace.maxRole,
                  phone: userWorkspace.phone,
                  comment: userWorkspace.comment
                }
              });
            }
            dataToSend = dataToImport;
          } else {
            dataToSend = transformedDataForApi;
          }

          console.info('dataToSend length:', dataToSend.length);
          console.info('About to call API:', url, 'with data:', dataToSend);
          // Envoi final via Orval
          const sendImport = async () => {
            switch (url) {
              case '/admin/companies/import':
                return await companiesImport({
                  data: dataToSend
                } as ImportCompanyDtoCompanyImportModel);
              case '/admin/users/import':
                return await usersImport({ data: dataToSend } as ImportUserDtoUserImportModel);
              case '/admin/points/import':
                return await pointsImport({ data: dataToSend } as ImportPointDtoPointImportModel);
              case '/admin/performances/import':
                return await perfsImport({
                  data: dataToSend
                } as ImportPerformanceDtoPerformanceImportModel);
              case '/admin/targets/import':
                return await targetsImport({
                  data: dataToSend
                } as ImportLogicTypeTargetStatusDtoTargetsImportModel);
            }
          };
          sendImport()
            .then((response: unknown) => {
              console.info('API call successful:', response);
              // Message de succès complet/partiel selon le nombre de lignes réellement importées
              const resp = response as { linesImported?: number } | undefined;
              const importedLines =
                typeof resp?.linesImported === 'number' ? resp.linesImported : dataToSend.length;
              const totalLines = data.length;
              let message = t('import.success');
              if (importedLines < totalLines) {
                message = t('import.success.partial', {
                  message: t('import.success'),
                  importedLines,
                  totalLines
                });
              }
              resolve({ success: true, message });
            })
            .catch((error) => {
              console.error('API call failed:', error);
              // Fallback succès si le serveur a bien accepté mais que la validation de réponse échoue
              if (
                (isOrvalError(error) &&
                  error.response?.status &&
                  error.response.status >= 200 &&
                  error.response.status < 300) ||
                !isOrvalError(error) /* ex: ZodError de parsing réponse */
              ) {
                resolve({ success: true, message: t('import.success') });
                return;
              }
              if (isOrvalError(error) && error.response?.status === 202) {
                // Traitement asynchrone accepté côté serveur (ex: en file)
                resolve({
                  success: true,
                  message:
                    forceImport && formatedErrors.length > 0
                      ? t('import.success.with.errors', {
                          message: t('import.success'),
                          importedLines: dataToSend.length,
                          totalLines: data.length
                        })
                      : t('import.success')
                });
                return;
              }

              if (isOrvalError(error) && error.response?.status === 422) {
                const apiErrors: ImportError[] = [];
                const serverErrorIndicesOnPost = new Set<number>();
                const violations = (
                  error.response.data as {
                    violations?: Array<{ property: string; detail: string }>;
                  }
                )?.violations;
                if (violations) {
                  // Mapper les violations Orval vers ViolationLike (ajouter message depuis detail)
                  const violationsLike = violations.map((v) => ({ ...v, message: v.detail }));
                  const mappedErrors = mapViolationsToErrorsWithIndexMap(
                    violationsLike,
                    indexMapToSend,
                    data as unknown as Record<string, unknown>[],
                    t
                  );
                  for (const err of mappedErrors) {
                    if (typeof err.line === 'number') {
                      const originalIndex = err.line - FIRST_DATA_LINE_NUMBER;
                      const postIdx = indexMapToSend.indexOf(originalIndex);
                      if (postIdx >= 0) serverErrorIndicesOnPost.add(postIdx);
                    }
                    apiErrors.push(err);
                  }
                }

                // Fusion + déduplication des erreurs locales et serveur
                const combinedErrors = dedupeErrors([...formatedErrors, ...apiErrors]);

                // Persiste pour un import forcé ultérieur (exclusion des lignes en erreur)
                setPreviousErrors(combinedErrors);

                if (forceImport) {
                  // Ré-essai automatique: on retire les lignes rejetées par le serveur et on renvoie
                  const reDataToSend = dataToSend.filter(
                    (_, idx) => !serverErrorIndicesOnPost.has(idx)
                  );

                  if (reDataToSend.length === 0) {
                    // Plus aucune ligne importable
                    resolve({
                      success: false,
                      message: t('import.process.no.lines.imported', { totalLines: data.length }),
                      errors: combinedErrors
                    });
                    return;
                  }

                  const resend = async () => {
                    switch (url) {
                      case '/admin/companies/import':
                        return await companiesImport({
                          data: reDataToSend
                        } as ImportCompanyDtoCompanyImportModel);
                      case '/admin/users/import':
                        return await usersImport({
                          data: reDataToSend
                        } as ImportUserDtoUserImportModel);
                      case '/admin/points/import':
                        return await pointsImport({
                          data: reDataToSend
                        } as ImportPointDtoPointImportModel);
                      case '/admin/performances/import':
                        return await perfsImport({
                          data: reDataToSend
                        } as ImportPerformanceDtoPerformanceImportModel);
                      case '/admin/targets/import':
                        return await targetsImport({
                          data: reDataToSend
                        } as ImportLogicTypeTargetStatusDtoTargetsImportModel);
                    }
                  };
                  resend()
                    .then((response2: unknown) => {
                      const resp2 = response2 as { linesImported?: number } | undefined;
                      const importedLines =
                        typeof resp2?.linesImported === 'number'
                          ? resp2.linesImported
                          : reDataToSend.length;
                      const totalLines = data.length;
                      let message = t('import.success');
                      if (importedLines < totalLines) {
                        message = t('import.success.partial', {
                          message: t('import.success'),
                          importedLines,
                          totalLines
                        });
                      }
                      resolve({ success: true, message });
                    })
                    .catch((err2) => {
                      if (
                        (isOrvalError(err2) &&
                          err2.response?.status &&
                          err2.response.status >= 200 &&
                          err2.response.status < 300) ||
                        !isOrvalError(err2)
                      ) {
                        resolve({ success: true, message: t('import.success') });
                        return;
                      }
                      // Deuxième tentative échouée -> on remonte la liste consolidée d'erreurs
                      resolve({ success: false, errors: combinedErrors });
                    });
                  return;
                }

                resolve({ success: false, errors: combinedErrors });
              } else {
                reject(t('import.process.error'));
              }
            });
        },
        skipEmptyLines: true,
        transform: transformCsvValue
      });
    });
  };

  return { onParse };
}
